import upload from 'utils/fileUploader';
import {
  GET_TEAM_MEMBERS,
  GET_TEAM,
  CREATE_TEAM_LABEL,
  UPDATE_TEAM,
  UPDATE_TEAM_PRODUCT_PERMISSION,
  GET_TEAM_ELIGIBLE_MEMBERS,
  CREATE_AND_INVITE_ADMINS,
  CREATE_TEAM_ADMIN,
  RESEND_TEAM_INVITE,
  DELETE_TEAM_ADMIN,
  GET_ACTIVE_USERS,
  GET_ASSIGNED_EVENT_TOOLS,
  CREATE_TEAM
} from 'actions/Types';

import { getCurrentTeamId, isAdminForTeam } from './utils';

import { showNotification, showModal } from 'actions';
import { PRE_TRIAL_TEAM_MODAL } from 'constants/ModalTypes';

import {
  PermissionStatuses,
  StripeProductPermissions
} from 'constants/Permissions';

export const updateProductPermissionByName = (name, changes) => {
  return (dispatch, getState) => {
    const teamId = getCurrentTeamId(getState);

    const action = {
      type: UPDATE_TEAM_PRODUCT_PERMISSION,
      request: async apiClient => {
        const team = await apiClient.team.updatePermissionByName(
          teamId,
          name,
          changes
        );

        dispatch({
          type: `${GET_TEAM}_SUCCESS`,
          payload: team
        });

        return team;
      }
    };

    dispatch(action);
  };
};

/**
 *
 * Resend an invite for a pending user
 *
 * @param {*} userId
 */
export const resendInviteInCurrentTeam = userId => {
  return (dispatch, getState) => {
    const teamId = getCurrentTeamId(getState);

    const action = {
      type: RESEND_TEAM_INVITE,
      request: async apiClient => {
        return await apiClient.team.resendInviteEmail(teamId, userId);
      }
    };
    dispatch(action);
  };
};

export const deleteAdminInCurrentTeam = userId => {
  return (dispatch, getState) => {
    const teamId = getCurrentTeamId(getState);

    const action = {
      type: DELETE_TEAM_ADMIN,
      request: async apiClient => {
        const result = await apiClient.team.deleteRole(userId, teamId);

        dispatch(getTeamMembers());
        dispatch(
          showNotification(
            `${result.firstName} ${result.lastName} has been successfully removed from this team!`
          )
        );
        return result;
      },
      payload: {
        teamId,
        userId
      }
    };

    dispatch(action);
  };
};

export const createAndInviteUsers = users => {
  return dispatch => {
    const action = {
      type: CREATE_AND_INVITE_ADMINS,
      request: async apiClient => {
        await Promise.all(
          users.map(async u => {
            let user = u.user;
            if (!user) {
              const result = await apiClient.user.findOrCreatePendingByEmail({
                emails: [u.email]
              });
              user = result[0];
            }

            dispatch(createAdminInCurrentTeam(user._id));
          })
        );
      }
    };

    dispatch(action);
  };
};

/**
 * Create role in an event
 *
 * @param {*} userId
 */
export const createAdminInCurrentTeam = userId => {
  return (dispatch, getState) => {
    const teamId = getCurrentTeamId(getState);

    const action = {
      type: CREATE_TEAM_ADMIN,
      request: async apiClient => {
        const result = await apiClient.team.createRole(teamId, {
          role: 'admin',
          userId
        });

        dispatch(getTeamMembers());

        return result;
      },
      payload: {
        userId
      }
    };

    dispatch(action);
  };
};

export const getEligibleMembers = () => {
  return (dispatch, getState) => {
    const teamId = getCurrentTeamId(getState);

    const action = {
      type: GET_TEAM_ELIGIBLE_MEMBERS,
      request: async apiClient => {
        return await apiClient.team.getEligibleMembers(teamId);
      }
    };

    dispatch(action);
  };
};

export const getTeamMembers = teamId => {
  return (dispatch, getState) => {
    if (!teamId) {
      teamId = getCurrentTeamId(getState);
    }

    if (!teamId || !isAdminForTeam(getState)) {
      const action = {
        type: GET_TEAM_MEMBERS,
        payload: []
      };
      return dispatch(action);
    }

    const action = {
      type: GET_TEAM_MEMBERS,
      request: async apiClient => {
        const members = await apiClient.team.getRoles(teamId);
        return members;
      }
    };

    dispatch(action);
  };
};

export const getTeam = teamId => {
  return (dispatch, getState) => {
    if (!teamId) {
      teamId = getCurrentTeamId(getState);
    }

    const action = {
      type: GET_TEAM,
      request: async apiClient => {
        const team = await apiClient.team.get(teamId);
        const teamPermissions = team ? team.permissions : [];
        const needsPretrialPermission = StripeProductPermissions.every(
          permName => {
            const permission = teamPermissions.find(
              perm => perm.name === permName
            );
            if (
              !permission ||
              (permission.status &&
                permission.status === PermissionStatuses.pretrial)
            ) {
              return true;
            }
            return false;
          }
        );
        if (needsPretrialPermission) {
          dispatch(showModal(PRE_TRIAL_TEAM_MODAL));
        }
        return team;
      }
    };

    dispatch(action);
  };
};

export const createTeamLabel = (teamId, label) => {
  return dispatch => {
    const action = {
      type: CREATE_TEAM_LABEL,
      request: async apiClient => {
        const team = await apiClient.team.createLabel(teamId, label);
        return team;
      }
    };

    dispatch(action);
  };
};

export const updateTeam = (teamId, changes, photoFile = null) => {
  return dispatch => {
    const action = {
      type: UPDATE_TEAM,
      request: async apiClient => {
        if (photoFile) {
          await _updatePhoto(teamId, photoFile);
        }
        const team = await apiClient.team.update(teamId, changes);
        return team;
      }
    };

    dispatch(action);
  };
};

const _updatePhoto = async (teamId, file) => {
  return await upload('/api/upload/team.updatePhoto', { teamId }, file);
};

export const getAssignedEventTools = () => {
  return (dispatch, getState) => {
    const teamId = getCurrentTeamId(getState);

    const action = {
      type: GET_ASSIGNED_EVENT_TOOLS,
      request: async apiClient => {
        const assignedEventTools = await apiClient.team.getAssignedEventTools(
          teamId
        );
        return assignedEventTools;
      }
    };

    dispatch(action);
  };
};

export const getActiveUsers = teamId => {
  return (dispatch, getState) => {
    const { teamEvents, team } = getState();

    const teamEventsMap = teamEvents.reduce((acc, curr) => {
      acc[curr._id] = curr.title;
      return acc;
    }, {});

    let assignedEventToolsMap = {};

    if (team.assignedEventTools) {
      assignedEventToolsMap = team.assignedEventTools.reduce((acc, curr) => {
        if (acc[curr.user]) {
          acc[curr.user].push(curr.tool);
        } else {
          acc[curr.user] = [curr.tool];
        }
        return acc;
      }, {});
    }

    const action = {
      type: GET_ACTIVE_USERS,
      request: async apiClient => {
        const activeUsers = await apiClient.team.getActiveUsers(teamId);

        const transformedActiveUsers = transformActiveUsers(
          activeUsers,
          teamEventsMap,
          assignedEventToolsMap
        );
        return transformedActiveUsers;
      }
    };

    dispatch(action);
  };
};

export const createTeam = createTeamDetails => {
  return (dispatch, getState) => {
    const action = {
      type: CREATE_TEAM,
      request: async apiClient => {
        const { user } = getState();
        const { title, industry } = createTeamDetails;

        const teamDetails = {
          title,
          email: user.email,
          industry
        };

        const team = await apiClient.team.create(teamDetails);

        const eventCreated = await apiClient.event.getByTeamId(team._id);

        window.location.assign(
          `/dash/team/${team._id}/event/${eventCreated.items[0]._id}`
        );

        return team;
      }
    };

    dispatch(action);
  };
};

const transformActiveUsers = (
  activeUsers,
  teamEventsMap,
  assignedEventToolsMap
) => {
  const activeUsersCopy = { ...activeUsers };
  const allUsers = Object.values(activeUsersCopy.users);

  const teamAdminMap = {};
  activeUsersCopy.teamUsers.forEach(user => {
    teamAdminMap[user] = user;
  });

  allUsers.forEach(item => {
    if (teamAdminMap[item._id]) {
      item.admin = 'Team Admin';
    } else {
      item.admin = '';
    }

    const transformedEvents = item.activeEvents.map(event => {
      return { _id: event, title: teamEventsMap[event] };
    });

    item.activeEvents = transformedEvents;
    item.assignedTools = assignedEventToolsMap[item._id] || [];
  });

  return activeUsersCopy;
};
